/**
 * The last line of defence.
 *
 * React unmounts the whole tree when a render throws, so without this a single
 * bad field in one question leaves a teacher looking at a white page with no way
 * back and nothing to report. This keeps the screen, says what to do, and offers
 * the one action that reliably works.
 */

import { Component } from 'react'
import type { ErrorInfo, ReactNode } from 'react'

type Props = { children: ReactNode }
type State = { error: Error | null }

export class ErrorBoundary extends Component<Props, State> {
  state: State = { error: null }

  static getDerivedStateFromError(error: Error): State {
    return { error }
  }

  componentDidCatch(error: Error, info: ErrorInfo) {
    // Kept in the console rather than shown: the message is for us, and a stack
    // trace on screen tells a teacher nothing they can act on.
    console.error('Unhandled render error', error, info.componentStack)
  }

  render() {
    if (!this.state.error) return this.props.children

    return (
      <div className="tc-boot" role="alert">
        <div className="tc-fatal">
          <h1>This screen stopped working</h1>
          <p>
            Nothing you did caused it, and no paper has been lost. Reloading
            usually clears it.
          </p>
          <button
            type="button"
            className="tc-btn tc-btn-primary"
            onClick={() => window.location.reload()}
          >
            Reload TeachCraftAI
          </button>
        </div>
      </div>
    )
  }
}
